import type {Agent} from '@helpers/detect';
import type {GetUnionLastValue, InitOptions} from '@helpers/type';

import {existsSync, renameSync} from 'node:fs';

import * as p from '@clack/prompts';
import chalk from 'chalk';
import {join} from 'pathe';

import {changeNpmrc} from '@helpers/actions/init/change-npmrc';
import {downloadTemplate} from '@helpers/fetch';
import {checkInitOptions} from '@helpers/init';
import {getPackageManagerInfo} from '@helpers/utils';
import {selectClack, taskClack, textClack} from 'src/prompts/clack';
import {resolver} from 'src/scripts/path';

import {ROOT} from '../../src/constants/path';
import {
  APP_DIR,
  APP_NAME,
  APP_REPO,
  PAGES_DIR,
  PAGES_NAME,
  PAGES_REPO,
  REACT_ROUTER_DIR,
  REACT_ROUTER_NAME,
  REACT_ROUTER_REPO,
  VITE_DIR,
  VITE_NAME,
  VITE_REPO
} from '../../src/constants/templates';

export const templatesMap: Record<Required<InitOptions>['template'], string> = {
  app: APP_NAME,
  pages: PAGES_NAME,
  'react-router': REACT_ROUTER_NAME,
  vite: VITE_NAME
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare let _exhaustiveCheck: never;

export async function initAction(_projectName?: string, options: InitOptions = {}) {
  const {package: _package, template: _template} = options;

  /** ======================== Check invalid options ======================== */
  checkInitOptions(_template, _package);

  /** ======================== Welcome title ======================== */
  p.intro(chalk.cyanBright('Create a new project'));

  /** ======================== Get the init info ======================== */
  const {packageName, projectName, template} = await getTableInfo(
    _package,
    _projectName,
    _template
  );
  const {run} = getPackageManagerInfo(packageName);

  /** ======================== Generate template ======================== */
  // Detect if the project name already exists
  if (existsSync(resolver(`${ROOT}/${projectName}`))) {
    p.cancel(`The project name ${chalk.redBright(projectName)} already exists`);
    process.exit(1);
  }

  if (template === 'app') {
    await generateTemplate(APP_REPO);
    renameTemplate(APP_DIR, projectName);
  } else if (template === 'pages') {
    await generateTemplate(PAGES_REPO);
    renameTemplate(PAGES_DIR, projectName);
  } else if (template === 'vite') {
    await generateTemplate(VITE_REPO);
    renameTemplate(VITE_DIR, projectName);
  } else if (template === 'react-router') {
    await generateTemplate(REACT_ROUTER_REPO);
    renameTemplate(REACT_ROUTER_DIR, projectName);
  } else {
    // If add new template and not update this template, it will be exhaustive check error
    _exhaustiveCheck = template;
  }

  const npmrcFile = resolver(`${ROOT}/${projectName}/.npmrc`);

  /** ======================== Change default npmrc content ======================== */
  changeNpmrc(npmrcFile);

  /** ======================== Add guide ======================== */
  p.note(
    `cd ${chalk.cyanBright(projectName)}\n${chalk.cyanBright(packageName)} install`,
    'Next steps'
  );

  p.outro(`🚀 Get started with ${chalk.cyanBright(`${packageName} ${run} dev`)}`);

  process.exit(0);
}

/** ======================== Helper function ======================== */
async function generateTemplate(url: string) {
  await taskClack({
    failText: 'Template creation failed',
    successText: 'Template created successfully!',
    task: downloadTemplate(ROOT, url),
    text: 'Creating template...'
  });
}

function renameTemplate(originName: string, projectName: string) {
  try {
    renameSync(join(ROOT, originName), join(ROOT, projectName));
  } catch (error) {
    if (error) {
      p.cancel(`rename Error: ${error}`);
      process.exit(1);
    }
  }
}

export type GenerateOptions<T, Last = GetUnionLastValue<T>> = [T] extends [never]
  ? []
  : [
      ...GenerateOptions<Exclude<T, Last>>,
      {
        label: string;
        value: Last;
        hint: string;
      }
    ];

async function getTableInfo(packageName?: string, projectName?: string, template?: string) {
  const options: GenerateOptions<Exclude<InitOptions['template'], undefined>> = [
    {
      hint: 'A Next.js 16 with app directory template pre-configured with HeroUI (v3) and Tailwind CSS.',
      label: 'App',
      value: 'app'
    },
    {
      hint: 'A Next.js 16 with pages directory template pre-configured with HeroUI (v3) and Tailwind CSS.',
      label: 'Pages',
      value: 'pages'
    },
    {
      hint: 'A Vite template pre-configured with HeroUI (v3) and Tailwind CSS.',
      label: 'Vite',
      value: 'vite'
    },
    {
      hint: 'A React Router template pre-configured with HeroUI (v3) and Tailwind CSS.',
      label: 'React Router',
      value: 'react-router'
    }
  ];

  if (!template) {
    template = (await selectClack({
      message: 'Select a template (Enter to select)',
      options
    })) as string;
  }

  if (!projectName) {
    projectName = (await textClack({
      initialValue: templatesMap[template as keyof typeof templatesMap],
      message: 'New project name (Enter to skip with default name)',
      placeholder: templatesMap[template as keyof typeof templatesMap]
    })) as string;
  }

  if (!packageName) {
    packageName = (await selectClack({
      message: 'Select a package manager (Enter to select)',
      options: [
        {
          label: chalk.gray('npm'),
          value: 'npm'
        },
        {
          label: chalk.gray('yarn'),
          value: 'yarn'
        },
        {
          label: chalk.gray('pnpm'),
          value: 'pnpm'
        },
        {
          label: chalk.gray('bun'),
          value: 'bun'
        }
      ]
    })) as Agent;
  }

  return {
    packageName: packageName as Agent,
    projectName,
    template: template as Exclude<InitOptions['template'], undefined>
  };
}
